import { Link } from 'react-router-dom'

const guide = {
  slug: 'how-to-block-porn-on-chrome',
  category: 'device',
  kicker: 'Chrome · Mac, Windows, iPhone',
  title: 'How to block porn on Chrome (and why an extension lasts about a week)',
  seoTitle: 'How to Block Porn on Chrome (and Keep It Blocked)',
  description: `Extensions, SafeSearch, a managed policy, a filtering DNS: four ways to block porn in Google Chrome, where each gets switched off, and the one that holds.`,
  published: '2026-09-03',
  minutes: 8,
  intro: `Chrome is the browser most people use on a computer and, often, on
          the phone too. It is also the easiest browser to unblock: extensions
          are a toggle, incognito switches them off, and a second Chrome
          profile starts clean. This guide walks through the four ways to
          block porn on Chrome, honestly, and then explains why the block that
          holds isn't in Chrome at all.`,
  next: ['does-incognito-bypass-porn-blockers', 'dns-porn-blocker', 'how-to-block-porn-on-windows'],
  body: (
    <>
      <h2>Method 1 — A blocking extension</h2>
      <p>
        The Chrome Web Store has dozens of site-blocking and adult-filtering
        extensions. Install one, add a password, and Chrome refuses the
        sites on its list. Two minutes.
      </p>
      <p>Where it stops, in order of how fast people find it:</p>
      <ul>
        <li><b>Incognito.</b> Extensions are off in incognito windows by default. Unless you explicitly allow the extension in incognito, a private window has no filter at all.</li>
        <li><b>Remove it.</b> <code>chrome://extensions</code>, Remove. A "password to uninstall" is a screen the extension draws; Chrome itself never asks for it.</li>
        <li><b>A new profile.</b> Chrome profiles are separate: a fresh profile has no extensions.</li>
        <li><b>Another browser.</b> Edge is already installed on Windows, Safari on Mac.</li>
      </ul>
      <p>
        An extension is a reminder that lives inside the thing it's supposed
        to restrain. Why private windows walk through it, layer by layer:
        <Link to="/guides/does-incognito-bypass-porn-blockers"> does incognito bypass porn blockers</Link>.
      </p>

      <h2>Method 2 — Lock SafeSearch</h2>
      <p>
        Google's SafeSearch hides explicit results in Search and Images. You
        can turn it on at <code>google.com/safesearch</code>, but on a device
        you control it's a toggle. The reliable way to lock it is at DNS
        level: when <code>www.google.com</code> resolves to Google's
        <code> forcesafesearch.google.com</code> address, every Google search
        from that device is filtered, in any browser, in any profile, in
        incognito. Bing and DuckDuckGo have the same mechanism. That's how
        SELFCNTRL enforces safe search: not a Chrome setting, a DNS answer.
      </p>

      <h2>Method 3 — Chrome's own management policies</h2>
      <p>
        Chrome can be managed like a company browser. On Windows, the
        <code> URLBlocklist</code> policy in the registry
        (<code>HKLM\Software\Policies\Google\Chrome\URLBlocklist</code>) and
        on Mac the same key in a managed preferences plist block domains
        Chrome will not open, and the user can't remove them from inside
        Chrome. <code>IncognitoModeAvailability = 1</code> removes incognito
        entirely. It's a real block for Chrome, and it's how schools and
        businesses do it.
      </p>
      <p>
        Where it stops: you're the administrator of your own computer. The
        registry key or the plist that you wrote, you can delete. And it's
        Chrome-only, by design.
      </p>

      <h2>Method 4 — A filtering DNS, under Chrome</h2>
      <p>
        Every request Chrome makes starts with the same question to the
        system: "where is this domain?" A <b>DNS category filter</b> refuses
        pornography at that step. The site never resolves, in Chrome, in
        incognito, in a new profile, in Edge, in an app that isn't a browser.
        The browser is no longer the thing you're fighting.
      </p>
      <p>
        One Chrome-specific detail matters here. Chrome can use its own
        encrypted DNS (<b>Secure DNS</b>, under Privacy and security) and
        skip the system resolver. In its default "automatic" mode Chrome only
        does that when the system resolver is one of the public providers it
        knows; a filtering resolver that isn't on that list keeps Chrome on
        the system path. But the second option, "With: custom provider",
        lets anyone type in a resolver of their choice, and Chrome will use
        it. That's a door, and it's in Chrome's own settings. (Firefox has
        the same feature, and honours a network canary domain,
        <code>use-application-dns.net</code>, to switch it off.) The whole
        mechanism, including the three ways around plain DNS filtering:
        <Link to="/guides/dns-porn-blocker"> DNS porn blockers explained</Link>.
      </p>

      <h2>What holds: the filter you can't switch back</h2>
      <p>
        Every method above shares one flaw: on your own computer, you hold
        the key. SELFCNTRL is the DNS filter from method 4 attached to your
        account instead of to a setting. On Mac and Windows it installs in a
        few minutes, enforces safe search, and adds curfews that cut the
        internet on a schedule. Tightening takes effect at once; loosening
        waits twenty-four hours, and the floor you set never drops. That
        delay is the difference between a block and a toggle.
      </p>
      <p>
        On iPhone and iPad, Chrome is just another app under a supervised
        profile that can't be removed and can't be joined by a VPN. Chrome on
        iOS doesn't even have its own DNS setting to fight. The device steps:
        <Link to="/guides/how-to-block-porn-on-windows"> Windows</Link>,
        <Link to="/guides/how-to-block-porn-on-mac"> Mac</Link>,
        <Link to="/guides/how-to-block-porn-on-iphone"> iPhone</Link>.
      </p>

      <h2>Chrome, side by side</h2>
      <table>
        <thead>
          <tr><th></th><th>Extension</th><th>SafeSearch toggle</th><th>Chrome policy</th><th>DNS filter (account-locked)</th></tr>
        </thead>
        <tbody>
          <tr><td>Blocks sites in Chrome</td><td>Yes</td><td>Results only</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Survives incognito</td><td>No (off by default)</td><td>No</td><td>Can remove incognito</td><td>Yes</td></tr>
          <tr><td>Survives a new profile</td><td>No</td><td>No</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Covers Edge, Safari, apps</td><td>No</td><td>No</td><td>No</td><td>Yes</td></tr>
          <tr><td>Off in under a minute</td><td>Yes</td><td>Yes</td><td>Yes (you're admin)</td><td>No (24-hour delay)</td></tr>
        </tbody>
      </table>
    </>
  ),
  faq: [
    {
      q: 'Do Chrome extensions work in incognito?',
      a: `Not unless you allow them one by one under chrome://extensions →
          Details → Allow in Incognito. By default a private window runs with
          no extensions, which is why an extension-based blocker is bypassed
          by Ctrl+Shift+N.`
    },
    {
      q: 'Can I block porn on Chrome for Android?',
      a: `Chrome on Android follows the device's DNS (Private DNS in Android
          settings) and Google's SafeSearch lock at DNS level. SELFCNTRL
          doesn't cover Android today: it locks iPhone, iPad, Mac and
          Windows. The methods above still apply to Chrome on a computer.`
    },
    {
      q: 'Does Chrome on iPhone bypass Screen Time?',
      a: `Screen Time's "Limit Adult Websites" applies to Safari and to apps
          that use Apple's web view; Chrome on iOS uses that web view, so it
          is generally covered, but the restriction is still undone by the
          Screen Time passcode. A DNS filter under a supervised profile
          covers Chrome without depending on that.`
    },
    {
      q: 'What about Chrome\'s Secure DNS setting?',
      a: `Chrome can resolve domains over its own encrypted DNS and skip the
          system resolver. Google designed a canary: if the network's
          resolver refuses use-application-dns.net, Chrome uses the system
          resolver instead. A filtering resolver that answers that way keeps
          Chrome inside the filter.`
    }
  ]
}

export default guide
